import * as nsfwjs from "nsfwjs";
import * as tf from "@tensorflow/tfjs";
import { useEffect, useState } from "react";
type ValidationResult =
    | { valid: true; url: string }
    | { valid: false; error: string };
export const useImageVerification = () => {
    const [model, setModel] = useState<nsfwjs.NSFWJS | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    useEffect(() => {
        // We use version 2.4.2 for the hosted model because it provides the standard model.json and .bin files.
        nsfwjs.load("https://unpkg.com/nsfwjs@2.4.2/models/mobilenet_v2/", {
            type: "layers",
        })
            .then((loadedModel: nsfwjs.NSFWJS) => {
                setModel(loadedModel);
            })
            .catch((err: any) => {
                console.error("Failed to load NSFW model:", err);
            });
    }, []);
    const isImageBlurry = (img: HTMLImageElement): boolean => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (!ctx) return true;
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
        let diff = 0;
        for (let i = 0; i < data.length - 4; i += 4) {
            const g1 = (data[i] + data[i + 1] + data[i + 2]) / 3;
            const g2 = (data[i + 4] + data[i + 5] + data[i + 6]) / 3;
            diff += Math.abs(g1 - g2);
        }
        const score = diff / (data.length / 4);
        console.log("Image blur score:", score);
        return score < 3; // lowered threshold to accommodate icons/simple images
    };
    const isNSFW = async (img: HTMLImageElement): Promise<boolean> => {
        if (!model) return false;
        const predictions = await model.classify(img);
        return predictions.some(
            (p: any) =>
                (p.className === "Porn" && p.probability > 0.7) ||
                (p.className === "Hentai" && p.probability > 0.7) ||
                (p.className === "Sexy" && p.probability > 0.8)
        );
    };
    const validateImage = async (file: File): Promise<ValidationResult> => {
        setLoading(true);
        const startTime = Date.now();
        const result = await new Promise<ValidationResult>((resolve) => {
            const url = URL.createObjectURL(file);
            if (file.type.startsWith("video/")) {
                resolve({ valid: true, url });
                return;
            }
            const img = new Image();
            img.src = url;
            img.onload = async () => {
                try {
                    // Blur check
                    if (isImageBlurry(img)) {
                        resolve({ valid: false, error: "Image is too blurry. Please upload a clearer image." });
                        return;
                    }
                    // NSFW check
                    const nsfw = await isNSFW(img);
                    if (nsfw) {
                        resolve({
                            valid: false,
                            error: "Inappropriate image detected",
                        });
                        return;
                    }
                    resolve({ valid: true, url });
                } catch (err) {
                    resolve({
                        valid: false,
                        error: "Unable to process the uploaded image. Please try another image.",
                    });
                }
            };
            img.onerror = () => {
                resolve({ valid: false, error: "Image file appears to be corrupted." });
            };
        });
        const elapsed = Date.now() - startTime;
        if (elapsed < 3000) {
            await new Promise((r) => setTimeout(r, 3000 - elapsed));
        }
        setLoading(false);
        return result;
    };
    return { validateImage, loading };
};